import React from 'react';
import { Truck, Edit, X, Calendar, Hash, Link, Save, Copy } from 'lucide-react';
import { Order } from '../../types/order';
import { formatDate } from '../../utils/date';

interface DeliveryInformationProps {
  order: Order;
  isEditingDelivery: boolean;
  setIsEditingDelivery: (value: boolean) => void;
  newDeliveryDate: string;
  setNewDeliveryDate: (value: string) => void;
  newTrackingId: string;
  setNewTrackingId: (value: string) => void;
  newTrackingUrl: string;
  setNewTrackingUrl: (value: string) => void;
  handleUpdateDeliveryInfo: () => void;
  copyToClipboard: (text: string) => void;
}

const DeliveryInformation: React.FC<DeliveryInformationProps> = ({
  order,
  isEditingDelivery,
  setIsEditingDelivery,
  newDeliveryDate,
  setNewDeliveryDate,
  newTrackingId,
  setNewTrackingId,
  newTrackingUrl,
  setNewTrackingUrl,
  handleUpdateDeliveryInfo,
  copyToClipboard
}) => {
  return (
    <div className="bg-white border-b border-gray-200">
      <div className="px-6 py-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center justify-between">
          <div className="flex items-center">
            <Truck className="w-5 h-5 mr-2" />
            Delivery Information
          </div>
          <button
            onClick={() => setIsEditingDelivery(!isEditingDelivery)}
            className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-600 hover:text-gray-900"
          >
            {isEditingDelivery ? (
              <>
                <X className="w-3 h-3 mr-1" />
                Cancel
              </>
            ) : (
              <>
                <Edit className="w-3 h-3 mr-1" />
                Edit
              </>
            )}
          </button>
        </h2>
      </div>
      <div className="px-6 py-4">
        <div className="space-y-4">
          {isEditingDelivery ? (
            <div className="space-y-4">
              {/* Expected Delivery Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Expected Delivery Date
                </label>
                <input
                  type="date"
                  value={newDeliveryDate}
                  onChange={(e) => setNewDeliveryDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Tracking ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Hash className="w-4 h-4 inline mr-1" />
                  Tracking ID
                </label>
                <input
                  type="text"
                  value={newTrackingId}
                  onChange={(e) => setNewTrackingId(e.target.value)}
                  placeholder="e.g., 1234567890, AWB123456"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              {/* Tracking URL */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  <Link className="w-4 h-4 inline mr-1" />
                  Tracking URL
                </label>
                <input
                  type="url"
                  value={newTrackingUrl}
                  onChange={(e) => setNewTrackingUrl(e.target.value)}
                  placeholder="https://example.com/track/123456"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={handleUpdateDeliveryInfo}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-indigo-600 hover:bg-indigo-700"
                >
                  <Save className="w-3 h-3 mr-1" />
                  Save Changes
                </button>
                <button
                  onClick={() => {
                    setIsEditingDelivery(false);
                    setNewDeliveryDate(order.expectedDeliveryDate ? order.expectedDeliveryDate.split('T')[0] : '');
                    setNewTrackingId(order.trackingId || '');
                    setNewTrackingUrl(order.trackingLink || '');
                  }}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {/* Expected Delivery Date */}
              <div>
                <span className="text-sm text-gray-600 flex items-center">
                  <Calendar className="w-4 h-4 mr-1" />
                  Expected Date:
                </span>
                <p className="text-sm text-gray-900 mt-1">
                  {order.expectedDeliveryDate ? formatDate(order.expectedDeliveryDate) : 'Not set'}
                </p>
              </div>

              {/* Tracking ID */}
              <div>
                <span className="text-sm text-gray-600 flex items-center">
                  <Hash className="w-4 h-4 mr-1" />
                  Tracking ID:
                </span>
                {order.trackingId ? (
                  <div className="flex items-center mt-1">
                    <p className="text-sm text-gray-900 mr-2">{order.trackingId}</p>
                    <button
                      onClick={() => copyToClipboard(order.trackingId!)}
                      className="inline-flex items-center px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                      title="Copy tracking ID"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-900 mt-1">Not set</p>
                )}
              </div>

              {/* Tracking URL */}
              <div>
                <span className="text-sm text-gray-600 flex items-center">
                  <Link className="w-4 h-4 mr-1" />
                  Tracking Link:
                </span>
                {order.trackingLink ? (
                  <div className="flex items-center mt-1">
                    <a
                      href={order.trackingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 hover:text-blue-800 underline mr-2 truncate max-w-xs"
                      title={order.trackingLink}
                    >
                      View Tracking
                    </a>
                    <button
                      onClick={() => copyToClipboard(order.trackingLink!)}
                      className="inline-flex items-center px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                      title="Copy tracking URL"
                    >
                      <Copy className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <p className="text-sm text-gray-900 mt-1">Not set</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DeliveryInformation;
